import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import useApi from '../../../hooks/useApi';
import apiService from '../../../services/apiService';
import Table from '../../../components/Table';
import Modal from '../../../components/Modal';
import FormInput from '../../../components/FormInput';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { FaTrash, FaEdit } from 'react-icons/fa';

function TermsAndConditions() {
  const { data: terms, loading: termsLoading, fetchData: fetchTerms } = useApi('/terms-and-conditions/all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTermsId, setEditingTermsId] = useState(null);
  const [termsForm, setTermsForm] = useState({
    title: '',
    content: '',
  });
  const [termsErrors, setTermsErrors] = useState({});

  const validateTermsForm = () => {
    const errors = {};
    if (!termsForm.title.trim()) errors.title = 'Title is required';
    if (!termsForm.content.trim()) errors.content = 'Content is required';
    setTermsErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleTermsInputChange = (e) => {
    const { name, value } = e.target;
    setTermsForm({ ...termsForm, [name]: value });
    setTermsErrors({ ...termsErrors, [name]: '' });
  };

  const handleEditorChange = (event, editor) => {
    const data = editor.getData();
    setTermsForm({ ...termsForm, content: data });
    setTermsErrors({ ...termsErrors, content: '' });
  };

  const handleTermsSubmit = async (e) => {
    e.preventDefault();
    if (!validateTermsForm()) {
      toast.error('Please fix form errors');
      return;
    }
    try {
      const formData = {
        title: termsForm.title,
        content: termsForm.content,
      };

      if (editingTermsId) {
        await apiService.request('put', `/terms-and-conditions/${editingTermsId}`, formData);
        toast.success('Terms and conditions updated successfully');
      } else {
        await apiService.post('/terms-and-conditions', formData);
        toast.success('Terms and conditions added successfully');
      }
      fetchTerms();
      setIsModalOpen(false);
      setTermsForm({
        title: '',
        content: '',
      });
      setEditingTermsId(null);
      setTermsErrors({});
    } catch (err) {
      // Error handled by apiService interceptor
    }
  };

  const handleEditTerms = (item) => {
    setTermsForm({
      title: item.title || '',
      content: item.content || '',
    });
    setEditingTermsId(item._id);
    setIsModalOpen(true);
    setTermsErrors({});
  };

  const handleDeleteTerms = async (termsId) => {
    if (!window.confirm('Are you sure you want to delete this terms and conditions?')) return;
    try {
      await apiService.delete(`/terms-and-conditions/${termsId}`);
      toast.success('Terms and conditions deleted successfully');
      fetchTerms();
    } catch (err) {
      // Error handled by apiService interceptor
    }
  };

  const termsColumns = [
    { key: 'title', label: 'Title', render: (item) => item.title || 'N/A' },
    { key: 'updatedAt', label: 'Last Updated', render: (item) => new Date(item.updatedAt).toLocaleString() || 'N/A' },
  ];

  const termsActions = [
    {
      label: 'Edit',
      className: 'text-yellow-500 hover:text-yellow-600',
      icon: <FaEdit />,
      onClick: handleEditTerms,
    },
    {
      label: 'Delete',
      className: 'text-red-500 hover:text-red-600',
      icon: <FaTrash />,
      onClick: (item) => handleDeleteTerms(item._id),
    },
  ];

  return (
    <div className="">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">Terms and Conditions Management</h2>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold dark:text-white">Terms and Conditions</h3>
        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Add Terms and Conditions
        </button>
      </div>
      {termsLoading ? (
        <div className="card text-center text-gray-600 dark:text-gray-300">Loading...</div>
      ) : !terms || terms.length === 0 ? (
        <div className="card text-center text-gray-600 dark:text-gray-300">No terms and conditions found.</div>
      ) : (
        <div className="card">
          <Table columns={termsColumns} data={terms || []} actions={termsActions} />
        </div>
      )}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setTermsForm({
            title: '',
            content: '',
          });
          setEditingTermsId(null);
          setTermsErrors({});
        }}
        title={editingTermsId ? 'Edit Terms and Conditions' : 'Add Terms and Conditions'}
        className="max-w-full lg:max-w-4xl mx-4"
      >
        <div className="max-h-[80vh] overflow-y-auto p-4">
          <form onSubmit={handleTermsSubmit}>
            <div className="grid grid-cols-1 gap-4">
              <FormInput
                label="Title"
                name="title"
                value={termsForm.title}
                onChange={handleTermsInputChange}
                required
                placeholder="Enter terms and conditions title"
                error={termsErrors.title}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Content</label>
                <div className="mt-1 ckeditor-container">
                  <CKEditor
                    editor={ClassicEditor}
                    data={termsForm.content}
                    onChange={handleEditorChange}
                    config={{
                      toolbar: [
                        'heading',
                        '|',
                        'bold',
                        'italic',
                        'underline',
                        'link',
                        '|',
                        'bulletedList',
                        'numberedList',
                        '|',
                        'blockQuote',
                        '|',
                        'undo',
                        'redo',
                      ],
                      language: 'en',
                    }}
                  />
                </div>
                {termsErrors.content && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{termsErrors.content}</p>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                type="button"
                onClick={() => {
                  setIsModalOpen(false);
                  setTermsForm({
                    title: '',
                    content: '',
                  });
                  setTermsErrors({});
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                disabled={termsLoading}
              >
                {editingTermsId ? 'Update Terms and Conditions' : 'Add Terms and Conditions'}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
}

export default TermsAndConditions;